import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import {
  convertPeriod,
  GRANULARITIES,
  lowerBound,
  periodTitle,
  step,
  unitStart,
  type CashflowView,
  type Granularity,
  type LocalDate,
} from './cashflowPeriod'

const NOUN: Record<Granularity, string> = { month: 'month', quarter: 'quarter', year: 'year' }

const same = (a: CashflowView, b: CashflowView) => a.period === b.period && a.granularity === b.granularity

/**
 * The page's period header (M3-R10, R11, R25): ‹ October 2026 · to date ›
 * and the Monthly / Quarterly / Yearly toggle. It reports a change and says
 * how it was made; it does not hold the view, and `partial` is decided by the
 * container (R10).
 *
 * The title is an `<h2>` (R22): the TopBar's `<h1>` "Cashflow" stays the
 * page's name. The title is not itself live (#720): a chart step already reads
 * its option through `aria-activedescendant`, so a live title read the period
 * twice. A polite region in the header says the new title only when the
 * header's own arrows or toggle made the change, because a button press
 * announces nothing of where it led.
 *
 * The arrows are `aria-disabled` at the ends, never native `disabled`: a
 * browser blurs a focused button the moment it becomes natively disabled, so
 * pressing Next onto today's period would drop a keyboard user to `<body>`
 * (the `BulkToolbar` precedent). An `aria-disabled` button still dispatches its
 * click, so each `onClick` returns early at its bound. They sit on the canvas,
 * beside the toggle, so they take the `page` hit target as it does.
 */
export function PeriodHeader({
  view,
  today,
  earliest,
  partial,
  shared,
  onChange,
}: {
  view: CashflowView
  today: LocalDate
  earliest: LocalDate | null
  partial: boolean
  /** `scope.shared`, for "Including 2 shared accounts" (M3-R25). */
  shared: number
  onChange: (next: CashflowView, how: 'prev' | 'next' | 'granularity') => void
}) {
  const g = view.granularity
  const noun = NOUN[g]
  // With no earliest transaction there is no floor — the body's empty state says why.
  const lower = lowerBound(earliest, g, today)
  const atStart = lower !== null && view.period <= lower
  const atEnd = view.period >= unitStart(today, g)
  // Phone: a smaller title, so "October 2026" fits between the arrows at 320px, and a full-width toggle.
  const phone = useBreakpoint() === 'phone'
  // The view this header last asked for. While the page shows it, the change was
  // the header's own and is announced. It is one-shot: the first time the page
  // shows any other view it is forgotten, so a chart walk that comes back onto
  // it stays silent (the listbox reads it). Only a change of the shown view
  // clears it, never the render between a click and the parent's update.
  const [asked, setAsked] = useState<CashflowView | null>(null)
  const [shown, setShown] = useState<CashflowView>(view)
  let remembered = asked
  if (shown.period !== view.period || shown.granularity !== g) {
    setShown(view)
    if (asked !== null && !same(asked, view)) {
      setAsked(null)
      remembered = null
    }
  }
  const mine = remembered !== null && same(remembered, view)
  const go = (next: CashflowView, how: 'prev' | 'next' | 'granularity') => {
    setAsked(next)
    onChange(next, how)
  }
  const title = periodTitle(view.period, g)

  return (
    <div className={phone ? 'cf-period cf-period--phone' : 'cf-period'}>
      <div className="cf-period__nav">
        <Button
          className="cf-period__step"
          size="page"
          aria-label={`Previous ${noun}`}
          aria-disabled={atStart || undefined}
          onClick={() => {
            if (!atStart) go({ ...view, period: step(view.period, g, -1) }, 'prev')
          }}
        >
          <Icon glyph={ChevronLeft} />
        </Button>
        <h2 className="cf-period__title">
          {title}
          {partial && (
            <>
              {' '}
              <span className="cf-period__partial cf-nowrap">· to date</span>
            </>
          )}
        </h2>
        <Button
          className="cf-period__step"
          size="page"
          aria-label={`Next ${noun}`}
          aria-disabled={atEnd || undefined}
          onClick={() => {
            if (!atEnd) go({ ...view, period: step(view.period, g, 1) }, 'next')
          }}
        >
          <Icon glyph={ChevronRight} />
        </Button>
      </div>
      <SegmentedControl
        label="Period"
        name="cashflow-period-granularity"
        options={GRANULARITIES}
        value={g}
        onChange={(to) => go({ granularity: to, period: convertPeriod(view.period, g, to, today) }, 'granularity')}
      />
      {/* Mounted throughout: a region inserted already holding its text is not announced. */}
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {mine ? `${title}${partial ? ' · to date' : ''}` : ''}
      </p>
      {shared > 0 && (
        <p className="cf-period__scope">
          Including {shared} shared {shared === 1 ? 'account' : 'accounts'}
        </p>
      )}
    </div>
  )
}
